// mdl.js – Quake alias models (.mdl) and sprites (.spr).

import { cstr } from './pak.js';

// Quake's 162 precomputed vertex normals (anorms.h), for shading.
export const ANORMS = anorms();

export class Mdl {
  constructor(buffer, name = '') {
    this.name = name;
    const dv = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    if (cstr(bytes, 0, 4) !== 'IDPO') throw new Error(`${name}: not an MDL`);
    this.scale = [dv.getFloat32(8, true), dv.getFloat32(12, true), dv.getFloat32(16, true)];
    this.origin = [dv.getFloat32(20, true), dv.getFloat32(24, true), dv.getFloat32(28, true)];
    this.radius = dv.getFloat32(32, true);
    const numSkins = dv.getInt32(48, true);
    this.skinW = dv.getInt32(52, true);
    this.skinH = dv.getInt32(56, true);
    this.numVerts = dv.getInt32(60, true);
    this.numTris = dv.getInt32(64, true);
    this.numFrames = dv.getInt32(68, true);
    this.flags = dv.getInt32(76, true);
    let p = 84;
    this.skins = [];
    for (let i = 0; i < numSkins; i++) {
      const group = dv.getInt32(p, true);
      p += 4;
      if (group === 0) {
        this.skins.push(bytes.subarray(p, p + this.skinW * this.skinH));
        p += this.skinW * this.skinH;
      } else {
        const n = dv.getInt32(p, true);
        p += 4 + 4 * n;
        this.skins.push(bytes.subarray(p, p + this.skinW * this.skinH)); // first of the group
        p += n * this.skinW * this.skinH;
      }
    }
    this.st = new Int32Array(this.numVerts * 3); // onseam, s, t
    for (let i = 0; i < this.numVerts; i++, p += 12) {
      this.st[i * 3] = dv.getInt32(p, true);
      this.st[i * 3 + 1] = dv.getInt32(p + 4, true);
      this.st[i * 3 + 2] = dv.getInt32(p + 8, true);
    }
    this.tris = new Int32Array(this.numTris * 4); // facesfront, v0, v1, v2
    for (let i = 0; i < this.numTris; i++, p += 16) {
      for (let k = 0; k < 4; k++) this.tris[i * 4 + k] = dv.getInt32(p + k * 4, true);
    }
    // frames: each is { name, verts: Uint8Array(numVerts*4) (x,y,z,normalIndex) }
    this.frames = [];
    for (let i = 0; i < this.numFrames; i++) {
      const type = dv.getInt32(p, true);
      p += 4;
      if (type === 0) {
        p += 8; // bboxmin, bboxmax
        const fname = cstr(bytes, p, 16);
        p += 16;
        this.frames.push({ name: fname, verts: bytes.subarray(p, p + this.numVerts * 4) });
        p += this.numVerts * 4;
      } else {
        const n = dv.getInt32(p, true);
        p += 4 + 8 + 4 * n; // min, max, intervals
        const sub = [];
        for (let k = 0; k < n; k++) {
          p += 8;
          const fname = cstr(bytes, p, 16);
          p += 16;
          sub.push({ name: fname, verts: bytes.subarray(p, p + this.numVerts * 4) });
          p += this.numVerts * 4;
        }
        // a frame group animates on its own at 10 Hz; the renderer picks by time
        this.frames.push({ name: sub[0].name, verts: sub[0].verts, group: sub });
      }
    }
  }

  /** Consecutive frames whose names share a prefix ("run1".."run6") → { run: {first, count} }. */
  animations() {
    const out = [];
    for (let i = 0; i < this.frames.length; i++) {
      const base = this.frames[i].name.replace(/\d+$/, '') || 'frame';   // LibreQuake names some frames "1", "2"…
      const last = out[out.length - 1];
      if (last && last.name === base) last.count++;
      else out.push({ name: base, first: i, count: 1 });
    }
    return out;
  }
}

export class Spr {
  constructor(buffer, name = '') {
    this.name = name;
    const dv = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    if (cstr(bytes, 0, 4) !== 'IDSP') throw new Error(`${name}: not a sprite`);
    this.type = dv.getInt32(8, true); // 0 vp parallel upright, 1 facing upright, 2 vp parallel, 3 oriented
    this.radius = dv.getFloat32(12, true);
    this.maxW = dv.getInt32(16, true);
    this.maxH = dv.getInt32(20, true);
    const n = dv.getInt32(24, true);
    let p = 36;
    this.frames = [];
    for (let i = 0; i < n; i++) {
      const group = dv.getInt32(p, true);
      p += 4;
      let count = 1;
      if (group !== 0) {
        count = dv.getInt32(p, true);
        p += 4 + 4 * count;
      }
      const sub = [];
      for (let k = 0; k < count; k++) {
        const ox = dv.getInt32(p, true), oy = dv.getInt32(p + 4, true);
        const w = dv.getInt32(p + 8, true), h = dv.getInt32(p + 12, true);
        p += 16;
        sub.push({ ox, oy, w, h, data: bytes.subarray(p, p + w * h) });
        p += w * h;
      }
      this.frames.push(sub[0]);
    }
  }
}

function anorms() {
  const a = [
    -0.525731, 0.000000, 0.850651, -0.442863, 0.238856, 0.864188, -0.295242, 0.000000, 0.955423, -0.309017, 0.500000, 0.809017,
    -0.162460, 0.262866, 0.951056, 0.000000, 0.000000, 1.000000, 0.000000, 0.850651, 0.525731, -0.147621, 0.716567, 0.681718,
    0.147621, 0.716567, 0.681718, 0.000000, 0.525731, 0.850651, 0.309017, 0.500000, 0.809017, 0.525731, 0.000000, 0.850651,
    0.295242, 0.000000, 0.955423, 0.442863, 0.238856, 0.864188, 0.162460, 0.262866, 0.951056, -0.681718, 0.147621, 0.716567,
    -0.809017, 0.309017, 0.500000, -0.587785, 0.425325, 0.688191, -0.850651, 0.525731, 0.000000, -0.864188, 0.442863, 0.238856,
    -0.716567, 0.681718, 0.147621, -0.688191, 0.587785, 0.425325, -0.500000, 0.809017, 0.309017, -0.238856, 0.864188, 0.442863,
    -0.425325, 0.688191, 0.587785, -0.716567, 0.681718, -0.147621, -0.500000, 0.809017, -0.309017, -0.525731, 0.850651, 0.000000,
    0.000000, 0.850651, -0.525731, -0.238856, 0.864188, -0.442863, 0.000000, 0.955423, -0.295242, -0.262866, 0.951056, -0.162460,
    0.000000, 1.000000, 0.000000, 0.000000, 0.955423, 0.295242, -0.262866, 0.951056, 0.162460, 0.238856, 0.864188, 0.442863,
    0.262866, 0.951056, 0.162460, 0.500000, 0.809017, 0.309017, 0.238856, 0.864188, -0.442863, 0.262866, 0.951056, -0.162460,
    0.500000, 0.809017, -0.309017, 0.850651, 0.525731, 0.000000, 0.716567, 0.681718, 0.147621, 0.716567, 0.681718, -0.147621,
    0.525731, 0.850651, 0.000000, 0.425325, 0.688191, 0.587785, 0.864188, 0.442863, 0.238856, 0.688191, 0.587785, 0.425325,
    0.809017, 0.309017, 0.500000, 0.681718, 0.147621, 0.716567, 0.587785, 0.425325, 0.688191, 0.955423, 0.295242, 0.000000,
    1.000000, 0.000000, 0.000000, 0.951056, 0.162460, 0.262866, 0.850651, -0.525731, 0.000000, 0.955423, -0.295242, 0.000000,
    0.864188, -0.442863, 0.238856, 0.951056, -0.162460, 0.262866, 0.809017, -0.309017, 0.500000, 0.681718, -0.147621, 0.716567,
    0.850651, 0.000000, 0.525731, 0.864188, 0.442863, -0.238856, 0.809017, 0.309017, -0.500000, 0.951056, 0.162460, -0.262866,
    0.525731, 0.000000, -0.850651, 0.681718, 0.147621, -0.716567, 0.681718, -0.147621, -0.716567, 0.850651, 0.000000, -0.525731,
    0.809017, -0.309017, -0.500000, 0.864188, -0.442863, -0.238856, 0.951056, -0.162460, -0.262866, 0.147621, 0.716567, -0.681718,
    0.309017, 0.500000, -0.809017, 0.425325, 0.688191, -0.587785, 0.442863, 0.238856, -0.864188, 0.587785, 0.425325, -0.688191,
    0.688191, 0.587785, -0.425325, -0.147621, 0.716567, -0.681718, -0.309017, 0.500000, -0.809017, 0.000000, 0.525731, -0.850651,
    -0.525731, 0.000000, -0.850651, -0.442863, 0.238856, -0.864188, -0.295242, 0.000000, -0.955423, -0.162460, 0.262866, -0.951056,
    0.000000, 0.000000, -1.000000, 0.295242, 0.000000, -0.955423, 0.162460, 0.262866, -0.951056, -0.442863, -0.238856, -0.864188,
    -0.309017, -0.500000, -0.809017, -0.162460, -0.262866, -0.951056, 0.000000, -0.850651, -0.525731, -0.147621, -0.716567, -0.681718,
    0.147621, -0.716567, -0.681718, 0.000000, -0.525731, -0.850651, 0.309017, -0.500000, -0.809017, 0.442863, -0.238856, -0.864188,
    0.162460, -0.262866, -0.951056, 0.238856, -0.864188, -0.442863, 0.500000, -0.809017, -0.309017, 0.425325, -0.688191, -0.587785,
    0.716567, -0.681718, -0.147621, 0.688191, -0.587785, -0.425325, 0.587785, -0.425325, -0.688191, 0.000000, -0.955423, -0.295242,
    0.000000, -1.000000, 0.000000, 0.262866, -0.951056, -0.162460, 0.000000, -0.850651, 0.525731, 0.000000, -0.955423, 0.295242,
    0.238856, -0.864188, 0.442863, 0.262866, -0.951056, 0.162460, 0.500000, -0.809017, 0.309017, 0.716567, -0.681718, 0.147621,
    0.525731, -0.850651, 0.000000, -0.238856, -0.864188, -0.442863, -0.500000, -0.809017, -0.309017, -0.262866, -0.951056, -0.162460,
    -0.850651, -0.525731, 0.000000, -0.716567, -0.681718, -0.147621, -0.716567, -0.681718, 0.147621, -0.525731, -0.850651, 0.000000,
    -0.500000, -0.809017, 0.309017, -0.238856, -0.864188, 0.442863, -0.262866, -0.951056, 0.162460, -0.864188, -0.442863, 0.238856,
    -0.809017, -0.309017, 0.500000, -0.688191, -0.587785, 0.425325, -0.681718, -0.147621, 0.716567, -0.442863, -0.238856, 0.864188,
    -0.587785, -0.425325, 0.688191, -0.309017, -0.500000, 0.809017, -0.147621, -0.716567, 0.681718, -0.425325, -0.688191, 0.587785,
    -0.162460, -0.262866, 0.951056, 0.442863, -0.238856, 0.864188, 0.162460, -0.262866, 0.951056, 0.309017, -0.500000, 0.809017,
    0.147621, -0.716567, 0.681718, 0.000000, -0.525731, 0.850651, 0.425325, -0.688191, 0.587785, 0.587785, -0.425325, 0.688191,
    0.688191, -0.587785, 0.425325, -0.955423, 0.295242, 0.000000, -0.951056, 0.162460, 0.262866, -1.000000, 0.000000, 0.000000,
    -0.850651, 0.000000, 0.525731, -0.955423, -0.295242, 0.000000, -0.951056, -0.162460, 0.262866, -0.864188, 0.442863, -0.238856,
    -0.951056, 0.162460, -0.262866, -0.809017, 0.309017, -0.500000, -0.864188, -0.442863, -0.238856, -0.951056, -0.162460, -0.262866,
    -0.809017, -0.309017, -0.500000, -0.681718, 0.147621, -0.716567, -0.681718, -0.147621, -0.716567, -0.850651, 0.000000, -0.525731,
    -0.688191, 0.587785, -0.425325, -0.587785, 0.425325, -0.688191, -0.425325, 0.688191, -0.587785, -0.425325, -0.688191, -0.587785,
    -0.587785, -0.425325, -0.688191, -0.688191, -0.587785, -0.425325,
  ];
  return new Float32Array(a);
}
